import { invariant } from '../../util/invariant';
import type { Fraction } from '../../util/fractionType';
import type { BerLength } from './types';

export interface ParserSource {
  remain(): number;
  readUint8(): number;
  readUint8Array(size: number): Uint8Array;
  subSource(size: number): ParserSource;
}

export class Parser {
  constructor(private source: ParserSource) { }

  public subParser(size: number): Parser {
    return new Parser(this.source.subSource(size));
  }

  public remain(): number {
    return this.source.remain();
  }

  public readUint8(): number {
    return this.source.readUint8();
  }

  public readUint8Array(size: number): Uint8Array {
    return this.source.readUint8Array(size);
  }

  public readUuid(): string {
    return this.readHex('bbbb-bb-bb-bb-bbbbbb');
  }

  public readUl(): string {
    return this.readHex('b.b.b.b.b.b.b.b.b.b.b.b.b.b.b.b');
  }

  public readLocalTag(): string {
    return this.readHex('b.b');
  }

  public readBerLength(): BerLength {
    const firstByte = this.source.readUint8();
    // eslint-disable-next-line no-bitwise
    if (firstByte & 0x80) {
      // eslint-disable-next-line no-bitwise
      const size = firstByte & 0x7F;
      if (size > 8) {
        throw new Error(`Invalid BER length. Number of bytes describing length is ${size}`);
      }
      let length = 0;
      for (let i = 0; i < size; i++) {
        length = (length * 0x100) + this.source.readUint8();
      }
      invariant(length <= Number.MAX_SAFE_INTEGER, `Ber length is too long. Max limit: ${Number.MAX_SAFE_INTEGER}`);
      return { bytesUsed: 1 + size, length };
    }

    // eslint-disable-next-line no-bitwise
    return { bytesUsed: 1, length: firstByte & 0x7F };
  }

  public readFraction(): Fraction {
    const numerator = this.readInt32BE();
    const denominator = this.readInt32BE();
    return { numerator, denominator };
  }

  public readString(length: number): string {
    const bytes = this.readUint8Array(length);
    return (new TextDecoder()).decode(bytes);
  }

  public readUInt8(): number {
    return this.source.readUint8();
  }

  public readInt8(): number {
    const u = this.source.readUint8();
    return (u > 127) ? u - 256 : u;
  }

  public readUInt16BE(): number {
    return this.readUnsignedBE(2);
  }

  public readInt16BE(): number {
    return this.readSignedBE(2);
  }

  public readUInt32BE(): number {
    return this.readUnsignedBE(4);
  }

  public readInt32BE(): number {
    return this.readSignedBE(4);
  }

  private readArrayHeader(): {count: number, size: number} {
    const count = this.readUInt32BE();
    const size = this.readUInt32BE();
    return {count, size};
  }

  public readInt32ArrayBE(): number[] {
    const {count, size} = this.readArrayHeader();
    invariant(size === 4, `Int32 array should have element size of 4, but found ${size}`);
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(this.readSignedBE(size));
    }
    return result;
  }

  public readUInt64BE(): number {
    return this.readUnsignedBE(8);
  }

  public readInt64BE(): number {
    return this.readSignedBE(8);
  }

  private readUnsignedBE(byteCount: number): number {
    let value = 0;
    for (let i = 0; i < byteCount; i++) {
      value = (value * 0x100) + this.source.readUint8();
    }
    return value;
  }

  private readSignedBE(byteCount: number): number {
    const firstByte = this.source.readUint8();
    const negative = firstByte >= 0x80;
    let value;
    if (negative) {
      value = 255 - firstByte;
      for (let i = 0; i < byteCount - 1; i++) {
        value = (value * 0x100) + (255 - this.source.readUint8());
      }
      value = (value * -1) - 1;
    } else {
      value = firstByte;
      for (let i = 0; i < byteCount - 1; i++) {
        value = (value * 0x100) + this.source.readUint8();
      }
    }
    return value;
  }

  /**
   * Substitutes every 'b' in format with the hex representation of a read byte.
   *
   * Example formats:
   * * Universal label: 'b.b.b.b.b.b.b.b.b.b.b.b.b.b.b.b'
   * * UUID: 'bbbb-bb-bb-bb-bbbbbb'
   */
  public readHex(format: string) {
    return format.replace(/b/g, () => this.source.readUint8().toString(16).padStart(2, '0'));
  }
}
